// Astrea Ops Platform - Event Form / Debrief data-access layer
// (event_forms / event_form_action_items / debrief_responses)
//
// Phase 3, seventh slice. project.eventForm is one big object (~100 fields across
// Event Info, Venue, Event Planning, plus nested actionItems[], onsiteTeam[], and a
// fixed 10-question debrief[]) - the schema in
// supabase/migrations/20260915000003_projects_and_lifecycle.sql already made the
// scope call for us here, rather than this needing its own Martin sign-off the way
// Tasks did: only the ~19 Event Info fields that are genuinely queryable business
// data got promoted to real columns on `event_forms`; everything else in the Venue
// and Event Planning sections stays in `event_forms.planning_fields jsonb`, a real,
// live, per-row bridge column (distinct from `projects.app_data`, which is the
// whole-project fallback used for a project untouched since this slice shipped).
// Writing the non-promoted fields into planning_fields on every save, rather than
// leaving them stuck in the stale app_data snapshot, keeps them live-synced too -
// the actual point of the migration - even though they're not individually
// queryable columns.
//
// Two deliberate scope cuts, matching the Blocked-By/comments precedent from the
// Tasks slice - deferred, not dropped, still readable via app_data fallback:
//   - `actual_attendance` (int) and the structured `children_attending`/
//     `children_age_ranges`/`childcare_provided`/`children_notes` columns exist on
//     `event_forms` ahead of any UI for them (the app still only has the old
//     free-text `attendeesStaffVendors`/`childrenFamilyAttending` fields) - left
//     unmapped this slice, they'll get wired up whenever that UI is actually built.
//   - `debrief_responses.owner_id` wants a real people.id uuid FK, but the app's
//     debrief "Owner" field is free text, same mismatch Tasks hit with Blocked By.
//     response/followUp/dueDate sync for real; `owner` keeps coming from app_data.
//
// `onsiteTeam[]` is in the data model (and has its own table,
// event_form_onsite_team) but has no live UI anywhere in EventFormTab - it's never
// rendered or edited - so it's left out of this slice entirely rather than syncing
// a field nothing produces data for.
//
// Architecture: same centralized-sync pattern the Tasks slice introduced, and an
// easier fit here than there - EventFormTab keeps the whole form in local `ef`
// state (useState) and only pushes it to the project via the single "Save Form"
// button (plus a couple of auto-save spots for client/venue selection), all of
// which already go through the same onUpdate -> updateProject funnel. So
// syncEventFormChanges() is called once from updateProject, keyed only on whether
// project.eventForm changed by reference - no per-field diffing needed the way
// Tasks required, since there's effectively one call site producing one whole-object
// write rather than ten scattered mutation functions.
//
// event_form_action_items and debrief_responses are children of one parent list
// each edited only through local state until Save - action items are synced with a
// delete-all-then-reinsert per save (ids aren't app-supplied; Postgres defaults
// them, since nothing depends on an action item's id surviving across saves),
// debrief_responses with an upsert keyed on the natural (project_id,
// question_number) unique constraint, since the 10 questions are fixed and never
// added or removed.

import { supabase } from './supabaseClient';
import { selectAllRes } from './selectAll';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
function safeDate(s) {
  return typeof s === 'string' && ISO_DATE_RE.test(s) ? s : null;
}

// [appKey, dbColumn] - the Event Info fields promoted to real columns on
// event_forms. Everything else in eventForm (excluding clientId/venueId,
// actionItems, onsiteTeam, debrief) goes into planning_fields instead.
const EVENT_FORM_COLUMNS = [
  ['clientCompany', 'client_company'],
  ['eventName', 'event_name'],
  ['timezone', 'timezone'],
  ['loadInOut', 'load_in_out'],
  ['leadPlannerClient', 'lead_planner_client_text'],
  ['leadPlannerLogistics', 'lead_planner_logistics_text'],
  ['leadTravelDirector', 'lead_travel_director_text'],
  ['productionLead', 'production_lead_text'],
  ['hotelVenueCSM', 'hotel_venue_csm_text'],
  ['directorSecurity', 'director_security_text'],
  ['attendeesStaffVendors', 'attendees_staff_vendors'],
  ['percentageHotel', 'percentage_hotel'],
  ['offsiteActivities', 'offsite_activities'],
  ['riskPicture', 'risk_picture'],
  ['knownThreatsClient', 'known_threats_client'],
  ['knownThreatsFacility', 'known_threats_facility'],
  ['recentLocalIncidents', 'recent_local_incidents'],
  ['specificThreatBriefRequired', 'specific_threat_brief_required'],
  ['overridingCircumstances', 'overriding_circumstances'],
];
const PLANNING_FIELDS_EXCLUDE = new Set([
  ...EVENT_FORM_COLUMNS.map(([appKey]) => appKey),
  'clientId', 'venueId', 'actionItems', 'onsiteTeam', 'debrief',
]);

// Loads event_forms + event_form_action_items + debrief_responses across every
// project in three queries, grouped by project_id - same "one query, group
// client-side" shape assignments.js/unavailability.js/tasks.js all use.
export async function loadEventFormsByProject() {
  const [
    { data: efRows, error: e1 },
    { data: aiRows, error: e2 },
    { data: drRows, error: e3 },
  ] = await Promise.all([
    selectAllRes(() => supabase.from('event_forms').select('*').order('project_id')),
    selectAllRes(() => supabase.from('event_form_action_items').select('*').order('created_at').order('id')),
    selectAllRes(() => supabase.from('debrief_responses').select('*').order('question_number').order('id')),
  ]);
  if (e1) throw e1;
  if (e2) throw e2;
  if (e3) throw e3;
  const efByProject = new Map((efRows || []).map(r => [r.project_id, r]));
  const aiByProject = new Map();
  (aiRows || []).forEach(r => {
    const list = aiByProject.get(r.project_id) || [];
    list.push(r);
    aiByProject.set(r.project_id, list);
  });
  const drByProject = new Map();
  (drRows || []).forEach(r => {
    const list = drByProject.get(r.project_id) || [];
    list.push(r);
    drByProject.set(r.project_id, list);
  });
  const projectIds = new Set([...efByProject.keys(), ...aiByProject.keys(), ...drByProject.keys()]);
  const byProject = new Map();
  projectIds.forEach(pid => {
    byProject.set(pid, {
      row: efByProject.get(pid) || null,
      actionItems: aiByProject.get(pid) || [],
      debriefResponses: drByProject.get(pid) || [],
    });
  });
  return byProject;
}

// Merges the authoritative event_forms/event_form_action_items/debrief_responses
// rows onto a project's app_data-sourced eventForm object. Falls back to
// appEventForm untouched if there's no real event_forms row yet (a project not
// touched since this slice landed). Real columns + planning_fields win for the
// Event Info/Venue/Event Planning scalars; action items are replaced wholesale
// once there's at least one real row; debrief keeps its app_data question/owner
// and overlays response/followUp/dueDate from the real rows, question by question.
export function mergeEventForm(appEventForm, realRow, actionItemRows, debriefResponseRows) {
  const base = appEventForm || {};
  if (!realRow) return base;
  const merged = { ...base };
  EVENT_FORM_COLUMNS.forEach(([appKey, dbKey]) => {
    if (realRow[dbKey] !== null && realRow[dbKey] !== undefined) merged[appKey] = realRow[dbKey];
  });
  if (realRow.planning_fields) Object.assign(merged, realRow.planning_fields);
  if (actionItemRows && actionItemRows.length > 0) {
    merged.actionItems = actionItemRows.map(r => ({
      id: r.id,
      item: r.item || '',
      responsibleParty: r.responsible_party || '',
      followUp: r.follow_up || '',
      dueDate: r.due_date || '',
      status: r.status || 'Open',
    }));
  }
  if (debriefResponseRows && debriefResponseRows.length > 0) {
    const byNum = new Map(debriefResponseRows.map(r => [r.question_number, r]));
    merged.debrief = (base.debrief || []).map(q => {
      const real = byNum.get(q.num);
      if (!real) return q;
      return { ...q, response: real.response || '', followUp: !!real.follow_up, dueDate: real.due_date || '' };
    });
  }
  return merged;
}

async function eventFormUpsert(projectId, eventForm) {
  const row = { project_id: projectId };
  EVENT_FORM_COLUMNS.forEach(([appKey, dbKey]) => { row[dbKey] = eventForm[appKey] || null; });
  const planning = {};
  Object.keys(eventForm).forEach(k => {
    if (!PLANNING_FIELDS_EXCLUDE.has(k)) planning[k] = eventForm[k];
  });
  row.planning_fields = planning;
  const { error } = await supabase.from('event_forms').upsert(row, { onConflict: 'project_id' });
  if (error) throw error;
}

async function actionItemsSync(projectId, actionItems) {
  const { error: delErr } = await supabase.from('event_form_action_items').delete().eq('project_id', projectId);
  if (delErr) throw delErr;
  const rows = (actionItems || []).map(ai => ({
    project_id: projectId,
    item: ai.item || null,
    responsible_party: ai.responsibleParty || null,
    follow_up: ai.followUp || null,
    due_date: safeDate(ai.dueDate),
    status: ai.status || 'Open',
  }));
  if (rows.length === 0) return;
  const { error: insErr } = await supabase.from('event_form_action_items').insert(rows);
  if (insErr) throw insErr;
}

async function debriefResponsesSync(projectId, debrief) {
  const rows = (debrief || []).map(q => ({
    project_id: projectId,
    question_number: q.num,
    response: q.response || null,
    follow_up: !!q.followUp,
    due_date: safeDate(q.dueDate),
  }));
  if (rows.length === 0) return;
  const { error } = await supabase.from('debrief_responses').upsert(rows, { onConflict: 'project_id,question_number' });
  if (error) throw error;
}

// Called once, centrally, from updateProject - see the module header for why one
// call site covers this whole slice. eventForm must be the project's current
// (post-update) eventForm object.
export async function syncEventFormChanges(projectId, eventForm) {
  if (!eventForm) return;
  await Promise.all([
    eventFormUpsert(projectId, eventForm),
    actionItemsSync(projectId, eventForm.actionItems),
    debriefResponsesSync(projectId, eventForm.debrief),
  ]);
}
